import type { ProjectOverview } from '../types/project';
import {
  VERDICT_ACCENT_TEXT,
  VERDICT_LABEL,
  VERDICT_PILL,
  VERDICT_RAIL,
} from './project-styles';

interface ProjectHeroProps {
  project: ProjectOverview;
}

/**
 * The bottom-line hero - the first and fastest read on the page. States the
 * verdict, the one-sentence headline, and the two-three takeaways that matter,
 * paired with the committed-vs-projected dates and the slip. Everything a
 * manager needs to grasp the project in three seconds sits here.
 */
export function ProjectHero({ project }: ProjectHeroProps) {
  const isOnTrack = project.verdict === 'on-track';
  const accentText = VERDICT_ACCENT_TEXT[project.verdict];

  return (
    <section className="relative overflow-hidden rounded-3xl border border-zinc-100 bg-white p-7 shadow-[0_40px_90px_-48px_rgba(16,27,58,0.55)] sm:p-9">
      {/* Standing rail. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-7 start-0 w-1.5 rounded-full ${
          VERDICT_RAIL[project.verdict]
        }`}
      />

      <div className="grid gap-8 lg:grid-cols-12">
        {/* The bottom line. */}
        <div className="lg:col-span-7">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
              {project.name} · {project.team}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
                VERDICT_PILL[project.verdict]
              }`}
            >
              {!isOnTrack && (
                <svg
                  viewBox="0 0 24 24"
                  className="h-3 w-3"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                </svg>
              )}
              {VERDICT_LABEL[project.verdict]}
            </span>
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-400">
              Bottom line
            </span>
          </div>

          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-black sm:text-4xl">
            {project.headline}
          </h1>

          <ul className="mt-6 flex flex-col gap-3">
            {project.bottomLines.map((bottomLine) => (
              <li key={bottomLine} className="flex items-start gap-3">
                <span
                  className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                    isOnTrack ? 'bg-emerald-500' : 'bg-rose-500'
                  }`}
                />
                <span className="text-[15px] leading-relaxed text-zinc-700">
                  {bottomLine}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* The numbers. */}
        <div className="flex flex-col justify-between gap-6 lg:col-span-5 lg:border-s lg:border-zinc-100 lg:ps-8">
          <div className="flex items-end gap-4">
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                Committed
              </span>
              <span className="mt-1 block text-2xl font-semibold tracking-tight text-zinc-900">
                {project.targetDate}
              </span>
            </div>
            <svg
              viewBox="0 0 24 24"
              className="mb-1.5 h-5 w-5 text-zinc-300 rtl:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                Projected
              </span>
              <span
                className={`mt-1 block text-2xl font-semibold tracking-tight ${accentText}`}
              >
                {project.projectedDate}
              </span>
            </div>
          </div>

          <div className="border-t border-zinc-100 pt-5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
              {isOnTrack ? 'Schedule' : 'Projected slip'}
            </span>
            <span
              className={`mt-1 block text-5xl font-bold leading-none tracking-tight ${
                isOnTrack ? 'text-emerald-600' : accentText
              }`}
            >
              {isOnTrack ? 'On time' : project.slipLabel}
            </span>
            <p className="mt-3 text-xs leading-relaxed text-zinc-500">
              {project.confidenceNote}
            </p>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                Scope complete
              </span>
              <span className="text-sm font-semibold text-zinc-700">
                {project.progressPct}%
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#1678C2] to-[#22D1C5]"
                style={{ width: `${project.progressPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stat strip - the relevant project details at a glance. */}
      <div className="mt-8 grid grid-cols-2 gap-y-5 border-t border-zinc-100 pt-6 sm:grid-cols-3 md:flex md:items-end md:gap-0">
        {project.stats.map((stat, statIndex) => (
          <div
            key={stat.label}
            className={`px-1 md:flex-1 md:px-6 ${
              statIndex > 0 ? 'md:border-s md:border-zinc-100' : 'md:ps-0'
            }`}
          >
            <div className="flex items-baseline gap-1">
              <span
                className={`text-3xl font-normal tracking-tight ${
                  stat.isUrgent ? 'text-rose-600' : 'text-zinc-900'
                }`}
              >
                {stat.value}
              </span>
              {stat.unit && (
                <span
                  className={`text-sm font-normal ${
                    stat.isUrgent ? 'text-rose-500' : 'text-zinc-400'
                  }`}
                >
                  {stat.unit}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-zinc-600">{stat.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
